<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

interface Props {
  modelValue: boolean;
  htmlContent: string;
  viewOnly?: boolean;
  title?: string;
  notice?: string;
}

const props = withDefaults(defineProps<Props>(), {
  viewOnly: false,
  title: "Terms & Conditions",
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "accept"): void;
  (e: "decline"): void;
}>();

const scrollContainer = ref<HTMLElement | null>(null);
const canShowActions = ref(false);
const showDeclineWarning = ref(false);

const getEl = (): HTMLElement | null => {
  const ref = scrollContainer.value as any;
  return ref?.$el ?? ref ?? null;
};

const checkScrollable = () => {
  const el = getEl();
  if (!el) return;
  if (el.scrollHeight <= el.clientHeight) {
    canShowActions.value = true;
  }
};

const onScroll = () => {
  const el = getEl();
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
    canShowActions.value = true;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      canShowActions.value = false;
      nextTick(() => {
        requestAnimationFrame(() => {
          const el = getEl();
          if (el) el.scrollTop = 0;
          checkScrollable();
        });
      });
    }
  }
);

const handleAccept = () => {
  emit("accept");
  emit("close");
};

const handleDeclineClick = () => {
  showDeclineWarning.value = true;
};

const handleCloseClick = () => {
  if (props.viewOnly) emit("close");
  else handleDeclineClick();
};

const handleBackToTerms = () => {
  showDeclineWarning.value = false;
};

const handleConfirmDecline = () => {
  showDeclineWarning.value = false;
  emit("decline");
};
</script>

<template>
  <VDialog
    :model-value="props.modelValue"
    :persistent="!props.viewOnly"
    max-width="750"
    scrollable
  >
    <VCard>
      <!-- Header -->
      <div class="d-flex align-center justify-space-between bg-dialogHeaderColor">
        <VCardTitle>{{ props.title }}</VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          @click="handleCloseClick"
        />
      </div>

      <VDivider />

      <VAlert v-if="props.notice" type="warning" variant="tonal" density="comfortable" class="ma-3 mb-0">
        {{ props.notice }}
      </VAlert>

      <!-- HTML content -->
      <VCardText ref="scrollContainer" style="overflow-y: auto; max-height: 60vh;" @scroll="onScroll">
        <div v-html="props.htmlContent" class="pa-2"></div>
      </VCardText>

      <VDivider />

      <!-- Footer: action mode -->
      <VCardActions v-if="!props.viewOnly" class="justify-end gap-3 mt-3">
        <template v-if="canShowActions">
          <VBtn color="error" variant="elevated" @click="handleDeclineClick">Decline</VBtn>
          <VBtn color="success" variant="elevated" @click="handleAccept">Accept</VBtn>
        </template>
        <span v-else class="text-caption text-medium-emphasis mr-2">Scroll to the bottom to accept or decline</span>
      </VCardActions>

      <!-- Footer: view-only mode -->
      <VCardActions v-else-if="props.viewOnly" class="justify-end gap-3 mt-3">
        <VBtn variant="elevated" @click="emit('close')">Close</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Decline confirmation -->
  <VDialog v-model="showDeclineWarning" max-width="480" persistent>
    <VCard>
      <VCardText class="pa-6">
        A hold has been placed on your benefits. As to continue to use our site and facilities you must agree to the terms, please contact us to discuss your concerns.
      </VCardText>
      <VCardActions class="justify-end gap-3 pb-4 px-4">
        <VBtn variant="flat" @click="handleBackToTerms">Back to Terms</VBtn>
        <VBtn color="error" variant="flat" @click="handleConfirmDecline">OK</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
